import React, { useState } from 'react';
import { 
  Flame, 
  Plus, 
  Search, 
  MapPin, 
  Eye, 
  CheckCircle, 
  XCircle, 
  Trash2, 
  Zap, 
  ShieldCheck, 
  Clock, 
  Ticket, 
  Users, 
  Radio, 
  SlidersHorizontal,
  Sparkles
} from 'lucide-react';
import CyberBadge from '../common/CyberBadge';
import CityPoolHeader from '../common/CityPoolHeader';

export default function PartyManagerView({
  rooms = [],
  onSelectDrawer,
  onOpenCreateModal,
  onApproveRoom,
  onRejectRoom,
  onDeleteRoom,
}) {
  const [activeSubTab, setActiveSubTab] = useState('all'); // 'all', 'live', 'pending', 'paused'
  const [selectedCity, setSelectedCity] = useState('Indore');
  const [searchTerm, setSearchTerm] = useState('');

  // Filter Warm Home & Wormhole Parties
  const parties = rooms.filter(
    (r) => r.categoryType === 'wormhole_party' || r.categoryType === 'party' || r.moduleData?.isWormhole || r.isWormhole
  );

  // Fallback demo parties if database has none
  const displayPartiesList = parties.length > 0 ? parties : [
    {
      _id: 'wh-p1',
      title: 'VIP Midnight Rooftop Warm Home Party',
      city: 'Indore',
      price: 499,
      priceType: 'ticket',
      categoryType: 'wormhole_party',
      status: 'approved',
      description: 'Exclusive late-night VIP Underground Party (11:30 PM - 5:00 AM) with live DJ, trap cyphers & lounge passes.',
      moduleData: { isWormhole: true, isAdminManaged: true, eventTime: '11:30 PM - 5:00 AM', coupleEntryAllowed: true },
      location: { address: 'C21 VIP Lounge, Indore', city: 'Indore' }
    },
    {
      _id: 'wh-p2',
      title: 'Indore Secret 3 AM Cypher House',
      city: 'Indore',
      price: 299,
      priceType: 'ticket',
      categoryType: 'wormhole_party',
      status: 'approved',
      description: 'Secret underground bass party hosted live on RapMap radar.',
      moduleData: { isWormhole: true, isAdminManaged: true, eventTime: '1:00 AM - 6:00 AM', coupleEntryAllowed: false },
      location: { address: 'Palasia Underground Basement, Indore', city: 'Indore' }
    },
    {
      _id: 'wh-p3',
      title: 'Midnight Trap & Bass After-Jam Submission',
      city: 'Indore',
      price: 350,
      priceType: 'ticket',
      categoryType: 'wormhole_party',
      status: 'pending',
      description: 'Community captain submitted late night party waiting for Admin verification.',
      moduleData: { isWormhole: true, isAdminManaged: false, eventTime: '12:00 AM - 4:00 AM', coupleEntryAllowed: true },
      location: { address: 'Vijay Nagar Club, Indore', city: 'Indore' }
    }
  ];

  // Filtering by City, Search, and Status Sub-Tab
  const filteredParties = displayPartiesList.filter((item) => {
    const matchesCity = !selectedCity || (item.city && item.city.toLowerCase().includes(selectedCity.toLowerCase())) || (item.location?.city && item.location.city.toLowerCase().includes(selectedCity.toLowerCase()));
    const matchesSearch = !searchTerm.trim() || 
      item.title?.toLowerCase().includes(searchTerm.toLowerCase()) || 
      item.description?.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (!matchesCity || !matchesSearch) return false;

    if (activeSubTab === 'live') return item.status === 'approved';
    if (activeSubTab === 'pending') return item.status === 'pending';
    if (activeSubTab === 'paused') return item.status === 'paused' || item.status === 'rejected';

    return true;
  });

  // Stat computations
  const totalParties = displayPartiesList.length;
  const liveCount = displayPartiesList.filter((p) => p.status === 'approved').length;
  const pendingCount = displayPartiesList.filter((p) => p.status === 'pending').length;
  const avgPrice = displayPartiesList.length > 0
    ? Math.round(displayPartiesList.reduce((acc, p) => acc + (parseFloat(p.price) || 0), 0) / displayPartiesList.length)
    : 0;

  return (
    <div className="animate-fade" style={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>
      {/* Header Banner */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <h1 className="page-title" style={{ fontSize: '1.75rem', fontWeight: 900, color: 'var(--text-primary)', margin: 0, display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Flame size={28} style={{ color: '#A78BFA' }} /> Warm Home Party Command Center
          </h1>
          <p style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', marginTop: '4px' }}>
            Full control panel to upload, manage, approve, pause, and feature late-night Warm Home & Wormhole Parties.
          </p>
        </div>

        <button
          className="btn btn-purple"
          style={{ padding: '12px 24px', borderRadius: '12px', fontSize: '0.9rem', fontWeight: 800, display: 'flex', alignItems: 'center', gap: '10px', boxShadow: '0 0 20px rgba(139, 92, 246, 0.3)' }}
          onClick={() => onOpenCreateModal('loka', 'wormhole_party')}
        >
          <Flame size={20} /> Upload Warm Home Party
        </button>
      </div>

      {/* Analytics Overview Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px' }}>
        <div className="cyber-card" style={{ padding: '20px', background: 'rgba(139, 92, 246, 0.1)', border: '1px solid rgba(139, 92, 246, 0.3)' }}>
          <div style={{ fontSize: '0.78rem', color: '#A78BFA', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Total Parties</div>
          <div style={{ fontSize: '1.8rem', fontWeight: 900, color: '#FFFFFF', marginTop: '6px' }}>{totalParties}</div>
          <div style={{ fontSize: '0.74rem', color: 'var(--text-muted)', marginTop: '4px' }}>RapMap Wormhole Radar</div>
        </div>

        <div className="cyber-card" style={{ padding: '20px', background: 'rgba(0, 255, 135, 0.1)', border: '1px solid rgba(0, 255, 135, 0.3)' }}>
          <div style={{ fontSize: '0.78rem', color: 'var(--neon-green)', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Live On Map</div>
          <div style={{ fontSize: '1.8rem', fontWeight: 900, color: 'var(--neon-green)', marginTop: '6px' }}>{liveCount}</div>
          <div style={{ fontSize: '0.74rem', color: 'var(--text-muted)', marginTop: '4px' }}>Active & Accepting Guests</div>
        </div>

        <div className="cyber-card" style={{ padding: '20px', background: 'rgba(255, 230, 0, 0.1)', border: '1px solid rgba(255, 230, 0, 0.3)' }}>
          <div style={{ fontSize: '0.78rem', color: 'var(--neon-yellow)', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Pending Queue</div>
          <div style={{ fontSize: '1.8rem', fontWeight: 900, color: 'var(--neon-yellow)', marginTop: '6px' }}>{pendingCount}</div>
          <div style={{ fontSize: '0.74rem', color: 'var(--text-muted)', marginTop: '4px' }}>Awaiting Admin Moderation</div>
        </div>

        <div className="cyber-card" style={{ padding: '20px', background: 'rgba(0, 240, 255, 0.1)', border: '1px solid rgba(0, 240, 255, 0.3)' }}>
          <div style={{ fontSize: '0.78rem', color: 'var(--neon-cyan)', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Avg Ticket Price</div>
          <div style={{ fontSize: '1.8rem', fontWeight: 900, color: 'var(--neon-cyan)', marginTop: '6px' }}>₹{avgPrice}</div>
          <div style={{ fontSize: '0.74rem', color: 'var(--text-muted)', marginTop: '4px' }}>VIP Entry Average</div>
        </div>
      </div>

      {/* City Selector Header */}
      <CityPoolHeader activeCity={selectedCity} onCityChange={setSelectedCity} />

      {/* Search & Sub-Tabs Bar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px', background: 'rgba(10,12,20,0.8)', padding: '12px 18px', borderRadius: '14px', border: '1px solid var(--border-color)' }}>
        {/* Sub-Tabs */}
        <div style={{ display: 'flex', gap: '8px' }}>
          {[
            { id: 'all', label: `All Parties (${displayPartiesList.length})` },
            { id: 'live', label: `Live (${liveCount})`, color: 'var(--neon-green)' },
            { id: 'pending', label: `Pending Approval (${pendingCount})`, color: 'var(--neon-yellow)' },
            { id: 'paused', label: 'Paused / Rejected' },
          ].map((tab) => {
            const isActive = activeSubTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveSubTab(tab.id)}
                style={{
                  padding: '8px 16px',
                  borderRadius: '8px',
                  fontSize: '0.82rem',
                  fontWeight: isActive ? 800 : 600,
                  background: isActive ? (tab.color ? `${tab.color}20` : 'rgba(255,255,255,0.1)') : 'transparent',
                  border: isActive ? `1.5px solid ${tab.color || 'var(--neon-purple)'}` : '1px solid transparent',
                  color: isActive ? '#FFFFFF' : 'var(--text-muted)',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Search Bar */}
        <div style={{ position: 'relative', width: '280px' }}>
          <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
          <input
            type="text"
            placeholder="Search parties by name or vibe..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '8px 12px 8px 36px',
              borderRadius: '8px',
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid var(--border-color)',
              color: '#FFFFFF',
              fontSize: '0.82rem',
              outline: 'none',
            }}
          />
        </div>
      </div>

      {/* PARTIES CARDS GRID */}
      {filteredParties.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', background: 'rgba(10,12,20,0.5)', borderRadius: '16px', border: '1px dashed var(--border-color)' }}>
          <Flame size={36} style={{ color: 'var(--text-muted)', marginBottom: '12px' }} />
          <div style={{ color: '#FFFFFF', fontSize: '1.1rem', fontWeight: 800 }}>No Warm Home Parties Found</div>
          <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginTop: '4px' }}>Click "Upload Warm Home Party" to publish a new late-night VIP event.</div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))', gap: '20px' }}>
          {filteredParties.map((party) => (
            <div
              key={party._id || party.id}
              style={{
                background: 'rgba(15, 17, 28, 0.85)',
                borderRadius: '16px',
                border: '1.5px solid rgba(139, 92, 246, 0.3)',
                padding: '20px',
                display: 'flex',
                flexDirection: 'column',
                gap: '14px',
                position: 'relative',
                boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
              }}
            >
              {/* Top Row: Title & Badges */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
                <div>
                  <div style={{ fontSize: '1.1rem', fontWeight: 900, color: '#FFFFFF', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    {party.title}
                  </div>
                  <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <MapPin size={12} color="#A78BFA" />
                    <span>{party.location?.address || party.city || 'Indore'}</span>
                  </div>
                </div>

                <CyberBadge status={party.status || 'approved'} />
              </div>

              {/* Description */}
              {party.description && (
                <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', background: 'rgba(255,255,255,0.03)', padding: '10px 14px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.06)' }}>
                  "{party.description}"
                </div>
              )}

              {/* Attributes Chips */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                <div style={{ padding: '4px 10px', borderRadius: '6px', background: 'rgba(139, 92, 246, 0.15)', border: '1px solid #8B5CF6', color: '#A78BFA', fontSize: '0.72rem', fontWeight: 800, display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <Clock size={12} /> {party.moduleData?.eventTime || party.eventTime || '11:30 PM - 5:00 AM'}
                </div>

                {party.moduleData?.isAdminManaged !== false && (
                  <div style={{ padding: '4px 10px', borderRadius: '6px', background: 'rgba(0, 255, 135, 0.15)', border: '1px solid var(--neon-green)', color: 'var(--neon-green)', fontSize: '0.72rem', fontWeight: 800, display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <ShieldCheck size={12} /> ADMIN MANAGED
                  </div>
                )}

                {party.moduleData?.coupleEntryAllowed && (
                  <div style={{ padding: '4px 10px', borderRadius: '6px', background: 'rgba(255, 0, 128, 0.15)', border: '1px solid var(--neon-pink)', color: 'var(--neon-pink)', fontSize: '0.72rem', fontWeight: 800 }}>
                    💑 COUPLE PASSES
                  </div>
                )}
              </div>

              {/* Price Row & Action Controls */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '12px', borderTop: '1px solid var(--border-color)', marginTop: 'auto' }}>
                <div>
                  <div style={{ fontSize: '1.25rem', fontWeight: 900, color: '#A78BFA', fontFamily: 'var(--font-mono)' }}>
                    ₹{party.price} <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>/ {party.priceType || 'ticket'}</span>
                  </div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--neon-green)', fontWeight: 700, marginTop: '2px' }}>
                    ● RAPMAP HOTZONE RADAR
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    className="btn btn-outline"
                    style={{ padding: '8px 12px', fontSize: '0.78rem' }}
                    onClick={() => onSelectDrawer(party, 'room')}
                    title="View & Edit Party Details"
                  >
                    <Eye size={14} /> Details
                  </button>

                  {party.status === 'pending' && (
                    <button
                      className="btn btn-emerald"
                      style={{ padding: '8px 12px', fontSize: '0.78rem', fontWeight: 800 }}
                      onClick={() => onApproveRoom(party._id || party.id)}
                    >
                      <CheckCircle size={14} /> Approve
                    </button>
                  )}

                  {party.status === 'approved' && onRejectRoom && (
                    <button
                      className="btn btn-outline"
                      style={{ padding: '8px 12px', fontSize: '0.78rem', borderColor: 'var(--neon-yellow)', color: 'var(--neon-yellow)' }}
                      onClick={() => onRejectRoom(party._id || party.id)}
                    >
                      Pause
                    </button>
                  )}

                  {onDeleteRoom && (
                    <button
                      className="btn btn-danger"
                      style={{ padding: '8px 10px' }}
                      onClick={() => onDeleteRoom(party._id || party.id)}
                      title="Delete Party Listing"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
